import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import type { OptionType } from '@/api/common';
import { useVendorList } from '@/api/vendor';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  type ComboboxProps,
} from '@/components/Form/pickers';

type Props = ComboboxProps<OptionType, true> & {
  disabled?: boolean;
  className?: string;
  value?: OptionType[];
};

const VendorMultiSelect = ({
  value,
  isLoading: isLoadingProp,
  size,
  disabled,
  className,
  'aria-invalid': ariaInvalid,
  ...rest
}: Props) => {
  const { vendors, isLoading } = useVendorList();

  const vendorOptions = useMemo<OptionType[]>(() => {
    return (
      vendors?.map((vendor) => {
        const name =
          vendor.channel?.name && vendor.name
            ? `${vendor.name} - ${vendor.channel?.name}`
            : (vendor.name ?? t`Unknown Vendor`);
        return {
          id: vendor.id,
          name,
        };
      }) ?? []
    );
  }, [vendors]);

  const vendorOptionsById = useMemo(
    () => new Map(vendorOptions.map((vendor) => [vendor.id, vendor])),
    [vendorOptions],
  );

  const resolvedValue = useMemo(() => {
    if (!value?.length) return [];
    return value
      .map((vendor) => vendorOptionsById.get(vendor.id) ?? vendor)
      .filter((vendor) => Boolean(vendor.name));
  }, [vendorOptionsById, value]);
  return (
    <Combobox
      data-testid="vendor-multiselect"
      {...rest}
      multiple
      items={isLoading ? [] : vendorOptions}
      itemToStringValue={(item) => item.id}
      itemToStringLabel={(item) => item.name ?? ''}
      isItemEqualToValue={(item, selected) => item.id === selected.id}
      isLoading={isLoadingProp || isLoading}
      disabled={disabled}
      size={size}
      value={resolvedValue}
    >
      <ComboboxChips className={className} aria-invalid={ariaInvalid}>
        <ComboboxValue>
          {resolvedValue.map((item) => (
            <ComboboxChip key={item.id}>{item.name}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput
          placeholder={isLoading ? t`Loading Vendors...` : t`Select Vendors...`}
        />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxEmpty>
          <Trans>No vendors were found...</Trans>
        </ComboboxEmpty>
        <ComboboxList>
          {(item: OptionType) => (
            <ComboboxItem key={item.id} value={item} disabled={item.disabled}>
              {item.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

export default VendorMultiSelect;
